import { useState, useMemo } from "react";
import api from "../../utils/axios";
import toast from "react-hot-toast";
import { FaTimes, FaChevronDown } from "react-icons/fa";

const inputClass =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none transition-all";

export default function AddDealer({ onClose, onCreated, gstData = [] }) {
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    gst: "",
    district: "",
    taluka: "",
    address: "",
    contactPerson: "",
    type: "dealer",
    target: {
      startDate: "2025-04-01",
      endDate: "2026-03-31",
    },
  });

  const [searchText, setSearchText] = useState("");
  const [gstSelected, setGstSelected] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedTaluka, setSelectedTaluka] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredGST = useMemo(() => {
    const search = searchText.toLowerCase().trim();

    return gstData
      .filter((d) => {
        const textMatch = !search
          ? true
          : (d.Particulars?.toLowerCase().includes(search) ||
              d.District?.toLowerCase().includes(search) ||
              d.TALUKA?.toLowerCase().includes(search));

        const districtMatch = selectedDistrict ? d.District === selectedDistrict : true;
        const talukaMatch   = selectedTaluka   ? d.TALUKA   === selectedTaluka   : true;

        return textMatch && districtMatch && talukaMatch;
      })
      .sort((a, b) => (a.Particulars || "").localeCompare(b.Particulars || ""));
  }, [gstData, searchText, selectedDistrict, selectedTaluka]);

  const handleGSTSelect = (d) => {
    setForm({
      ...form,
      name: d.Particulars || "",
      mobile: d["Contact No"]?.toString() || "",
      gst: d["GSTIN/UIN"] || "",
      district: d.District || "",
      taluka: d.TALUKA || "",
      address: d["Address(As Per GST No. Address)"] || "",
      contactPerson: d["Contact Person Name"] || "",
    });

    setSearchText(d.Particulars || "");
    setGstSelected(true);
    setShowDropdown(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTargetChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      target: { ...prev.target, [name]: value },
    }));
  };

  const districts = useMemo(() => {
    return [...new Set(gstData.map((d) => d.District).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b)
    );
  }, [gstData]);

  const talukas = useMemo(() => {
    return [
      ...new Set(
        gstData
          .filter((d) => d.District === selectedDistrict)
          .map((d) => d.TALUKA)
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b));
  }, [gstData, selectedDistrict]);

  const handleClearFilters = () => {
    setSelectedDistrict("");
    setSelectedTaluka("");
    setSearchText("");
    setShowDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) {
      toast.error("Name and Mobile are required");
      return;
    }

    try {
      await api.post("/admin/dealers/create", form);
      toast.success("Dealer created successfully");
      onCreated?.();
      onClose();
      window.location.reload();
    } catch (err) {
      toast.error("Failed to create dealer");
      console.error(err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl p-6 shadow-2xl overflow-y-auto max-h-[92vh] relative">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Add New Dealer</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 p-1 rounded-full hover:bg-gray-100"
          >
            <FaTimes size={22} />
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <select className={inputClass} value={selectedDistrict} onChange={(e) => {
            setSelectedDistrict(e.target.value);
            setSelectedTaluka("");
          }}>
            <option value="">All Districts</option>
            {districts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>

          <select className={inputClass} value={selectedTaluka} onChange={(e) => setSelectedTaluka(e.target.value)} disabled={!selectedDistrict}>
            <option value="">All Talukas</option>
            {talukas.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <button onClick={handleClearFilters} className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-100 whitespace-nowrap">
            Clear Filters
          </button>
        </div>

        {/* ────────────────────── Search Party ────────────────────── */}
        {!gstSelected && (
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-1.5">Search Party</label>

            <div className="relative">
              <input
                className={`${inputClass} pr-20`}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Search by Party / District / Taluka"
              />

              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <FaChevronDown
                  className={`text-gray-500 transition-transform ${showDropdown ? "rotate-180" : ""}`}
                  size={16}
                />
              </div>

              {searchText && (
                <button
                  type="button"
                  className="absolute inset-y-0 right-10 flex items-center text-gray-400 hover:text-gray-700 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchText("");
                    setShowDropdown(true);
                  }}
                >
                  <FaTimes size={14} />
                </button>
              )}

              {/* Dropdown – using fixed positioning to appear outside modal scroll */}
              {showDropdown && (
                <div
                  className={`
                    fixed z-[100]
                    mt-1.5
                    w-full max-w-[min(90vw,640px)]
                    bg-white border border-gray-300 rounded-xl
                    shadow-2xl max-h-[70vh] overflow-y-auto
                    divide-y divide-gray-100
                  `}
                  style={{
                    top: 'auto',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    // You can also try calculating position dynamically with useRef + getBoundingClientRect
                  }}
                >
                  {filteredGST.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500 text-sm">
                      No matching records found
                    </div>
                  ) : (
                    filteredGST.map((d, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          handleGSTSelect(d);
                          setShowDropdown(false);
                        }}
                        className="px-5 py-4 cursor-pointer hover:bg-yellow-50 active:bg-yellow-100 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{d.Particulars || "—"}</div>
                        <div className="text-sm text-gray-600 mt-0.5">
                          {d.District || "—"} • {d.TALUKA || "—"}
                        </div>
                        {d["GSTIN/UIN"] && (
                          <div className="text-xs text-gray-400 mt-1 font-mono break-all">
                            {d["GSTIN/UIN"]}
                          </div>
                        )}
                      </div>
                    ))
                  )}

                  {filteredGST.length > 0 && (
                    <div className="px-5 py-3 text-xs text-gray-500 text-right bg-gray-50 sticky bottom-0">
                      {filteredGST.length} record{filteredGST.length !== 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form after selection */}
        {gstSelected && (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input className={inputClass} name="name" value={form.name} onChange={handleChange} placeholder="Dealer Name *" required />
            <input className={inputClass} name="mobile" value={form.mobile} onChange={handleChange} placeholder="Mobile Number *" required />
            <input className={inputClass} name="gst" value={form.gst} onChange={handleChange} placeholder="GSTIN" />
            <input className={inputClass} name="district" value={form.district} onChange={handleChange} placeholder="District" />
            <input className={inputClass} name="taluka" value={form.taluka} onChange={handleChange} placeholder="Taluka" />
            <input className={inputClass} name="contactPerson" value={form.contactPerson} onChange={handleChange} placeholder="Contact Person" />

            <div>
              <label className="text-xs text-gray-600 block mb-1">Target Start Date</label>
              <input type="date" name="startDate" value={form.target.startDate} onChange={handleTargetChange} className={inputClass} />
            </div>

            <div>
              <label className="text-xs text-gray-600 block mb-1">Target End Date</label>
              <input type="date" name="endDate" value={form.target.endDate} onChange={handleTargetChange} className={inputClass} />
            </div>

            <textarea
              className={`${inputClass} h-28 md:col-span-2 resize-none`}
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Full Address"
            />

            <div className="md:col-span-2 flex gap-4 mt-3">
              <button type="submit" className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-xl font-semibold transition">
                Create Dealer
              </button>
              <button type="button" onClick={() => setGstSelected(false)} className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition">
                Back to Search
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}